import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { getContent, adminUpdateSingleton } from '@/lib/api';
import { SINGLETONS } from '@/pages/admin/crudConfigs';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';

export default function SingletonEditor() {
  const { name } = useParams();
  const meta = SINGLETONS[name];
  const [form, setForm] = useState({});
  const [rawJson, setRawJson] = useState({});
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    getContent(name).then((d) => {
      const clean = { ...d };
      delete clean.id; delete clean.updatedAt; delete clean._id;
      setForm(clean);
      const raw = {};
      Object.entries(clean).forEach(([k, v]) => {
        if (v && typeof v === 'object') raw[k] = JSON.stringify(v, null, 2);
      });
      setRawJson(raw);
    }).catch(() => {});
  }, [name]);

  if (!meta) return <div className='p-8'>Unknown section.</div>;

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setJson = (k, str) => {
    setRawJson((r) => ({ ...r, [k]: str }));
    try { const parsed = JSON.parse(str); setForm((f) => ({ ...f, [k]: parsed })); setErrors((e) => ({ ...e, [k]: null })); }
    catch (err) { setErrors((e) => ({ ...e, [k]: 'Invalid JSON' })); }
  };

  const save = async () => {
    if (Object.values(errors).some(Boolean)) { toast.error('Fix JSON errors before saving'); return; }
    setLoading(true);
    try { await adminUpdateSingleton(name, form); toast.success('Saved'); }
    catch (e) { toast.error('Save failed'); }
    finally { setLoading(false); }
  };

  return (
    <div>
      <div className='flex items-center justify-between'>
        <div>
          <h1 className='font-head text-2xl font-semibold text-[#20272A]'>{meta.label}</h1>
          <p className='text-sm text-[#20272A]/50'>Structured content editor</p>
        </div>
        <button onClick={save} disabled={loading} className='rounded-lg bg-[#075B61] px-5 py-2.5 text-sm font-medium text-white hover:bg-[#0a6d74] disabled:opacity-60' data-testid='singleton-save-button'>{loading ? 'Saving…' : 'Save Changes'}</button>
      </div>

      <div className='mt-6 grid gap-5'>
        {Object.entries(form).map(([key, val]) => {
          if (typeof val === 'boolean') {
            return (
              <div key={key} className='flex items-center gap-3 rounded-xl border border-[#20272A]/10 bg-white p-4'>
                <Switch checked={val} onCheckedChange={(v) => setField(key, v)} />
                <Label className='text-sm text-[#20272A]/70'>{key}</Label>
              </div>
            );
          }
          if (val && typeof val === 'object') {
            return (
              <div key={key} className='rounded-xl border border-[#20272A]/10 bg-white p-4'>
                <Label className='text-xs font-medium uppercase tracking-wider text-[#075B61]'>{key} (JSON)</Label>
                <Textarea value={rawJson[key] ?? ''} onChange={(e) => setJson(key, e.target.value)} className='mt-2 min-h-[160px] font-mono-num text-xs' />
                {errors[key] && <p className='mt-1 text-xs text-red-600'>{errors[key]}</p>}
              </div>
            );
          }
          const long = typeof val === 'string' && val.length > 80;
          return (
            <div key={key} className='rounded-xl border border-[#20272A]/10 bg-white p-4'>
              <Label className='text-xs font-medium uppercase tracking-wider text-[#20272A]/50'>{key}</Label>
              {long ? (
                <Textarea value={val} onChange={(e) => setField(key, e.target.value)} className='mt-2 min-h-[90px]' />
              ) : (
                <Input value={val ?? ''} onChange={(e) => setField(key, e.target.value)} className='mt-2 h-10' />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
